import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import AppRoutes from './AppRoutes';
import SessionWarningModal from './components/SessionWarningModal';
import LoadingSpinner from './components/common/LoadingSpinner';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ErrorProvider } from './contexts/ErrorContext';
import { sessionManager } from './utils/sessionManager';
import { CONFIG } from './config/config';

function AppContent() {
  const { isLoggedIn, user, isInitializing, logout } = useAuth();
  const [showSessionWarning, setShowSessionWarning] = useState(false);
  const [sessionRemainingTime, setSessionRemainingTime] = useState(CONFIG.SESSION_WARNING_MINUTES * 60); // Convert minutes to seconds
  const location = useLocation();
  const onLoginPage = location.pathname === '/login';
  const wasLoggedInRef = useRef(isLoggedIn);

  const handleSessionTimeout = useCallback(async () => {
    setShowSessionWarning(false);
    console.log('App: Session timeout, calling AuthContext logout...');
    await logout(true); // Pass true to indicate this is a session timeout
  }, [logout]);

  // Reset session warning when user logs out or goes to login page
  useEffect(() => {
    const wasLoggedIn = wasLoggedInRef.current;
    wasLoggedInRef.current = isLoggedIn;
    
    // User just logged out or went to login page - reset warning
    if ((wasLoggedIn && !isLoggedIn) || onLoginPage) {
      setShowSessionWarning(false);
    }
  }, [isLoggedIn, onLoginPage]);

  // Session warning and timeout setup - only when logged in
  useEffect(() => {
    if (isLoggedIn && !onLoginPage) {
      // IMPORTANT: Set callbacks FIRST before resetting timers
      // This ensures callbacks are in place when timers fire
      sessionManager.setWarningCallback(() => {
        console.log('App: Warning callback triggered');
        setSessionRemainingTime(CONFIG.SESSION_WARNING_MINUTES * 60); // Convert minutes to seconds
        setShowSessionWarning(true);
      });

      sessionManager.setTimeoutCallback(() => {
        handleSessionTimeout();
      });

      // Ensure session management is active and timers are reset with callbacks
      // Always reset timers after setting callbacks to ensure they're used
      if (sessionManager.isSessionManagementActive()) {
        // Session is active, reset timers to ensure callbacks are used
        sessionManager.resetSessionTimer();
      } else {
        // Session not active, enable it (this will also reset timers)
        // enableSessionManagement checks for sessionData internally
        sessionManager.enableSessionManagement();
      }
    } else {
      // Disable session management when not logged in or on login page
      sessionManager.disableSessionManagement();
    }

    return () => {
      // Clean up on unmount or dependency change
      if (!isLoggedIn || onLoginPage) {
        sessionManager.clearTimers();
      }
    };
  }, [isLoggedIn, onLoginPage, handleSessionTimeout]);

  const handleExtendSession = () => {
    sessionManager.updateActivity();
    setShowSessionWarning(false);
  };

  const handleLogout = async () => {
    await handleSessionTimeout();
  };

  // Show loading screen while initializing session
  if (isInitializing) {
    return <LoadingSpinner overlay size="large" />;
  }

  return (
    <>
      <AppRoutes
        clientName={user?.emailId || ''}
        setClientName={() => { }} // Managed by AuthContext
        userRole={user?.role || ''}
        setUserRole={() => { }} // Managed by AuthContext
      />
      {!onLoginPage && (
        <SessionWarningModal
          isVisible={showSessionWarning}
          remainingTime={sessionRemainingTime}
          onExtendSession={handleExtendSession}
          onLogout={handleLogout}
        />
      )}
    </>
  );
}

function App() {
  return (
    <ErrorProvider>
      <AuthProvider>
        <AppContent />
      </AuthProvider>
    </ErrorProvider>
  );
}

export default App;
